import { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  addSuggestions,
  checkCombinations,
  createCombinations,
  fetchChannelName,
  removeSuggestions,
  setBrandNames,
  setChannelNames,
  setProductNames,
  sort,
  uncheckCombinations,
} from '../keywordSlice'
import KeywordContentEditableInput from 'components/form/KeywordContentEditableInput'
import classNames from 'classnames'
import { hashCode } from '../../../../utils'
import CheckBox from '../../../../components/form/CheckBox'
import TableBody from '../../../../components/table/TableBody'
import Table from '../../../../components/table/Table'
import { fail } from '../../../toast/Toast'
import Tooltip from '../../../../components/tooltip/Tooltip'

const CombinationKeywords = () => {
  const dispatch = useDispatch()
  const {
    campaign: {
      form: { bizChannelId },
    },
    keyword: {
      isChannelNamesLoading,
      form: {
        channelNames,
        brandNames,
        productNames,
        combinations,
        suggestions,
        asc,
      },
    },
  } = useSelector(state => state.ad)
  const disabled = useMemo(
    () =>
      isChannelNamesLoading ||
      channelNames.length + brandNames.length + productNames.length === 0,
    [isChannelNamesLoading, channelNames, brandNames, productNames]
  )

  useEffect(() => {
    if (bizChannelId) dispatch(fetchChannelName())
  }, [dispatch, bizChannelId])

  const handleChannelNameChange = useCallback(
    value => dispatch(setChannelNames(value)),
    [dispatch]
  )
  const handleBrandNameChange = useCallback(
    value => dispatch(setBrandNames(value)),
    [dispatch]
  )
  const handleProductNameChange = useCallback(
    value => dispatch(setProductNames(value)),
    [dispatch]
  )
  const handleCreateCombinations = useCallback(() => {
    if (
      channelNames.some(name => name.length > 20) ||
      brandNames.some(name => name.length > 20) ||
      productNames.some(name => name.length > 20)
    ) {
      fail('최대 20글자 단어만 입력 가능합니다.')
    } else {
      dispatch(createCombinations())
    }
  }, [channelNames, brandNames, productNames, dispatch])
  const isAllChecked = useMemo(() => {
    const filtered = combinations.filter(
      combination => combination.length <= 50
    )
    return (
      filtered.length > 0 &&
      filtered.every(combination => suggestions.includes(combination))
    )
  }, [suggestions, combinations])
  const handleCheckboxChange = useCallback(
    event => {
      const { target } = event
      const { value, checked } = target
      if (checked) {
        dispatch(addSuggestions(value))
      } else {
        dispatch(removeSuggestions(value))
      }
    },
    [dispatch]
  )
  const handleCheckAll = useCallback(
    checked => {
      if (checked) {
        dispatch(checkCombinations())
      } else {
        dispatch(uncheckCombinations())
      }
    },
    [dispatch]
  )
  const handleSort = useCallback(() => dispatch(sort()), [dispatch])

  return (
    <div className="box_result">
      <div className="top_result">
        <div className="result_tit">
          <strong className="tit_result">키워드 조합</strong>
          <span className="desc_tit">
            입력한 채널명, 브랜드명, 상품명을 바탕으로 키워드를 조합하여 추천해
            줍니다.{' '}
            <Tooltip
              wrapperClassName="link_desc"
              content={
                <>
                  <strong className="tit_tooltip">키워드 조합 방식 안내</strong>
                  <ul className="list_tooltip">
                    <li>
                      <span className="ico_dot" />
                      채널명
                    </li>
                    <li>
                      <span className="ico_dot" />
                      브랜드명
                    </li>
                    <li>
                      <span className="ico_dot" />
                      브랜드명 + 상품명
                    </li>
                    <li>
                      <span className="ico_dot" />
                      채널명 - 브랜드명
                    </li>
                    <li>
                      <span className="ico_dot" />
                      채널명 - 브랜드명 + 상품명
                    </li>
                    <li>
                      <span className="ico_dot" />
                      상품명만 단독 노출되지 않습니다.
                    </li>
                  </ul>
                </>
              }>
              키워드 조합 방식 안내
            </Tooltip>
          </span>
        </div>
        <div className="result_group">
          <KeywordContentEditableInput
            keywords={channelNames}
            onChange={handleChannelNameChange}
            tooltip="연결된 채널과 연관 있는 키워드를 입력하세요."
            title="채널명"
          />
          <KeywordContentEditableInput
            keywords={brandNames}
            onChange={handleBrandNameChange}
            tooltip="홍보할 브랜드와 연관 있는 키워드를 입력하세요."
            title="브랜드명"
          />
          <KeywordContentEditableInput
            keywords={productNames}
            onChange={handleProductNameChange}
            tooltip="홍보할 상품과 연관 있는 키워드를 입력하세요."
            title="상품명"
          />
        </div>
        <div
          className={classNames('result_btn', {
            in_active: disabled,
          })}>
          <button
            type="button"
            className={classNames('btn_result', {
              in_active: disabled,
            })}
            disabled={disabled}
            onClick={handleCreateCombinations}>
            조합하기
          </button>
        </div>
      </div>
      <div className="result_tit">
        <strong className="tit_result">조합 키워드 선택</strong>
      </div>
      <div className="tblg2_wrap">
        <div className="tblg2_head">
          <Table className="tbl_g2" caption="키워드 제안 결과 테이블">
            <colgroup>
              <col width={42} />
              <col />
            </colgroup>
            <thead>
              <tr>
                <th scope="col" className="align_c">
                  <div className="inner_tbl">
                    <div className="hide_shim" />
                    <CheckBox
                      className="check_inp"
                      checked={isAllChecked}
                      onCheckedChange={handleCheckAll}
                    />
                  </div>
                </th>
                <th scope="col" className="sortable" onClick={handleSort}>
                  <div className="inner_tbl">
                    <div className="hide_shim" />
                    키워드
                    <span
                      className={classNames('ico_comm ico_sort', {
                        sort_up: asc,
                        sort_down: !asc,
                      })}>
                      정렬
                    </span>
                  </div>
                </th>
              </tr>
            </thead>
          </Table>
        </div>
        <div className="tblg_body">
          <div className="inner_table">
            <Table className="tbl_g2" caption="키워드 제안 결과 테이블">
              <colgroup>
                <col width={42} />
                <col />
              </colgroup>
              <TableBody
                column={2}
                empty={combinations.length === 0}
                emptyMessage={
                  <div className="inner_tbl">
                    조합된 키워드가 없습니다. <br />
                    상단에서 키워드를 조합하여 키워드를 찾으세요.
                  </div>
                }>
                {combinations.map(combination => {
                  const checked = suggestions.includes(combination)
                  const overflow = combination.length > 50
                  return (
                    <tr
                      key={hashCode(combination)}
                      className={classNames({ on: checked })}>
                      <td className="align_c">
                        <div className="inner_tbl">
                          <CheckBox
                            className="check_inp"
                            value={combination}
                            checked={checked}
                            disabled={overflow}
                            onChange={handleCheckboxChange}
                          />
                        </div>
                      </td>
                      <td>
                        <div className="inner_tbl">
                          <span className="txt_limit">{combination}</span>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
      <div className="item_info">
        <ul className="list_dot">
          <li>
            조합된 키워드는 심사승인을 보장하지 않으며, 키워드 사용에 따른 모든
            법적 책임은 광고주에게 있습니다.
          </li>
          <li>
            톡검색 서비스 특성상 친구/채팅 찾는 니즈와 겹치는 경우 일부
            키워드에서 추천소재 미노출 될 수 있습니다.
          </li>
        </ul>
      </div>
    </div>
  )
}
export default CombinationKeywords
